import { FC, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Grid } from '@mui/material';
import { ScanBarcodeIcon } from 'lucide-react';

import { FormCard, RenderFormField } from '@/components';
import { ScanIDModal } from '@/components/Modals/ScanIDModal';
import { Button } from '@/components/TailwindComponents/base/Button/Button';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import { useVerifiedFields } from '@/contexts';
import { formatHeightDisplay } from '@/utils/common';
import { ParsedIDCard } from '@/utils/idCardParser';

import { LicenseInfoFormFields } from '../data';

interface LicenseInfoSectionProps {
  disabled?: boolean;
}

export const LicenseInfoSection: FC<LicenseInfoSectionProps> = ({
  disabled = false,
}) => {
  const { getValues, register, watch } = useFormContext();
  const { isFieldVerified, applyScannedData } = useVerifiedFields();
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const driverLicenseState = watch('driverLicense.state');
  const driverLicenseNumber = watch('driverLicense.number');
  const requireDriverLicenseState = Boolean(driverLicenseNumber?.trim?.());
  const requireDriverLicenseNumber = Boolean(driverLicenseState);

  const handleOpenScanModal = useCallback(() => {
    if (disabled) {
      return;
    }
    setIsScanModalOpen(true);
  }, [disabled]);

  const handleCloseScanModal = useCallback(() => {
    setIsScanModalOpen(false);
  }, []);

  const handleApplyScannedData = useCallback(
    (selectedFields: Partial<ParsedIDCard>) => {
      applyScannedData(selectedFields);
      setIsScanModalOpen(false);
    },
    [applyScannedData],
  );

  const getCurrentValues = useCallback((): Partial<ParsedIDCard> => {
    const values = getValues();
    return {
      idNumber: values.driverLicense?.number || '',
      customerIdNumber: values.driverLicense?.customerIdNumber || '',
      documentNumber: values.driverLicense?.documentNumber || '',
      state: values.driverLicense?.state || '',
      issueDate: values.driverLicense?.issueDate || '',
      expirationDate: values.driverLicense?.expireDate || '',
      revisionDate: values.driverLicense?.revisionDate || '',
      vehicleClass: values.driverLicense?.vehicleClass || '',
      restrictionCodes: values.driverLicense?.restrictionCodes || '',
      endorsementCodes: values.driverLicense?.endorsementCodes || '',
      documentDiscriminator: values.driverLicense?.documentDiscriminator || '',
      lastName: values.lastName || '',
      firstName: values.firstName || '',
      middleName: values.middleName || '',
      nameSuffix: values.suffix || '',
      dateOfBirth: values.birthday || '',
      sex: values.sex || '',
      eyeColor: values.physicalCharacteristics?.eyeColor || '',
      height: formatHeightDisplay(values.physicalCharacteristics?.heightExact),
      addressNumber: values.addresses?.[0]?.addressNumber || '',
      streetAddress1: values.addresses?.[0]?.completeStreetName || '',
      streetAddress2: values.addresses?.[0]?.subAddress || '',
      city: values.addresses?.[0]?.placeName || '',
      addressState: values.addresses?.[0]?.state || '',
      zipCode: values.addresses?.[0]?.zipcode || '',
      country: values.addresses?.[0]?.country || '',
    };
  }, [getValues]);

  const customTitle = (
    <div className="flex justify-between items-center w-full cardTitle">
      <span className="font-medium">License info</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleOpenScanModal}
        disabled={disabled}
        className="flex items-center gap-1 text-sm underline"
      >
        <ScanBarcodeIcon className="w-4 h-4" />
        Scan
      </Button>
    </div>
  );

  const getLicenseFieldProps = (field: any) => {
    if (field.name === 'driverLicense.state') {
      return {
        ...field,
        rules: {
          ...field.rules,
          required: requireDriverLicenseState
            ? 'This field is required when ID number is entered.'
            : false,
        },
      };
    }

    if (field.name === 'driverLicense.number') {
      return {
        ...field,
        rules: {
          ...field.rules,
          required: requireDriverLicenseNumber
            ? 'This field is required when license state is entered.'
            : false,
        },
      };
    }

    return field;
  };

  return (
    <>
      <FormCard
        className="grey-title"
        title={customTitle}
        style={{ fontWeight: 500 }}
      >
        {LicenseInfoFormFields.map(({ grid, ...rest }: any, index: number) => {
          const fieldProps = getLicenseFieldProps(rest);

          return fieldProps.type === 'hidden' ? (
            <input
              key={`license-${index}`}
              type="hidden"
              {...register(fieldProps.name)}
            />
          ) : (
            <Grid key={`license-${index}`} xs={12} sm={6} md={3} {...grid} item>
              <div className="flex items-end w-full">
                <div className="flex-1">
                  <RenderFormField {...fieldProps} disabled={disabled} />
                </div>
                {isFieldVerified(fieldProps.name) && (
                  <VerifiedBadge
                    tooltip="Verified by state ID"
                    className="mb-4"
                  />
                )}
              </div>
            </Grid>
          );
        })}
      </FormCard>

      <ScanIDModal
        open={!disabled && isScanModalOpen}
        onClose={handleCloseScanModal}
        onApply={handleApplyScannedData}
        currentValues={getCurrentValues()}
      />
    </>
  );
};

export default LicenseInfoSection;
